import { useEffect, useState, useMemo, useRef } from "react";
import { devLog } from "../utils/devLog";
import { useCheckout } from "../context/CheckoutContext";
import { resolveLineItemQuantity } from "../utils/lineItemDisplay";
import { useLanguage } from "../context/LanguageContext";
import { useMetadata } from "./useMetadata";
import { checkDomain } from "../api/authentication";
import { fetchCheckoutConf, fetchCheckoutDetails } from "../api/api";
import { toast } from "react-toastify";

// Helper Functions
const extractDomain = () => window.location.hostname;

const getInitialFormData = () => ({
  firstName: "",
  lastName: "",
  email: "",
  billingAddress: {
    firstName: "",
    lastName: "",
    street: "",
    addressLine2: "",
    city: "",
    zipCode: "",
    state: "",
    country: "",
  },
  shippingAddress: {
    firstName: "",
    lastName: "",
    street: "",
    addressLine2: "",
    city: "",
    zipCode: "",
    state: "",
    country: "",
  },
  useSameShippingAddress: false,
  paymentMethod: null,
});

const getInitialItemDetails = () => ({
  billingPlatformName: "",
  planName: "",
  name: "",
  price: "",
  period: "",
  periodUnit: "",
  currencyCode: "",
});

// Only a subset of billing platforms require country to run a usable
// initial estimate before the user fills billing details.
const COUNTRY_REQUIRED_ESTIMATE_PROVIDERS = new Set(["frisbii"]);

/**
 * Shared hook for checkout page logic
 * Used by both CheckoutPage and AltruonCheckoutPage
 */
export const useCheckoutPage = (options = {}) => {
  const {
    // Options for different initialization strategies
    initialBillingPlatformId = null,
    initialPlanId = null,
    initialFrequency = null,
    initialPrice = null,
    initialCurrency = null,
    sessionId = null,
    /**
     * Defer catalog fetch until session exists (avoids fetching once without
     * sessionId and again when sessionId arrives). Prefer true for all
     * CheckoutPage modes (hosted + legacy).
     */
    catalogRequiresSessionId = false,
    // Callback for session-based initialization (AltruonCheckoutPage)
    onSessionDataFetched = null,
  } = options;

  const {
    setClientIp,
    setClientCountry,
    setLogoUrl,
    setBrandColor,
    setMerchantName,
  } = useCheckout();
  const { autoSelectLanguageFromCurrency } = useLanguage();
  const { updateMetadata } = useMetadata();
  // State
  const [checkoutPageConfDto, setCheckoutPageConfDto] = useState();
  const [checkoutPageFields, setCheckoutPageFields] = useState();
  const [routingEntries, setRoutingEntries] = useState([]);
  const [platformItemDetails, setPlatformItemDetails] = useState(null);
  const [isErrorModalVisible, setErrorModalVisible] = useState(false);
  const [itemDetails, setItemDetails] = useState(getInitialItemDetails());
  const [showModal, setShowModal] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [domainStatus, setDomainStatus] = useState("checking");
  const [currentDomain, setCurrentDomain] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState(getInitialFormData());
  const [isFormValid, setIsFormValid] = useState(false);
  // Validity of the customer information form, reported by CustomerInfoForm.
  // This respects which fields are actually required (optional fields left
  // blank do not invalidate the form). The CTA validity is derived from this
  // plus a selected payment method, so the two validators no longer conflict.
  const [customerFormValid, setCustomerFormValid] = useState(false);
  const [isRoutingLoaded, setIsRoutingLoaded] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [isPaymentProcessingModalVisible, setIsPaymentProcessingModalVisible] = useState(false);
  const [isTransactionDetailsModalVisible, setIsTransactionDetailsModalVisible] = useState(false);
  const [paymentProcessingHeader, setPaymentProcessingHeader] = useState("Processing your payment...");
  const [paymentProcessingMessage, setPaymentProcessingMessage] = useState("Please wait while we process your payment.");
  const geoLookupAttemptedRef = useRef(false);
  /** Skip full-page loading / skin unmount on catalog refetch. */
  const catalogLoadedOnceRef = useRef(false);
  /** Ignore stale catalog responses when deps change mid-flight. */
  const catalogRequestIdRef = useRef(0);

  // Derived state (can be set externally for AltruonCheckoutPage)
  const [billingPlatformId, setBillingPlatformId] = useState(initialBillingPlatformId);
  const [planId, setPlanId] = useState(initialPlanId);
  const [selectedAddons, setSelectedAddons] = useState(options.initialAddons || []);
  const [frequency, setFrequency] = useState(initialFrequency);
  const [price, setPrice] = useState(initialPrice);
  const [currency, setCurrency] = useState(initialCurrency);

  const resolvedBillingPlatformId = billingPlatformId || initialBillingPlatformId;
  const resolvedPlanId = planId || initialPlanId;
  const resolvedCurrency = currency || initialCurrency;
  const resolvedFrequency = frequency || initialFrequency;
  const resolvedPrice = price ?? initialPrice;

  const addons = useMemo(() => {
  return (
    platformItemDetails?.filter(
      item => item.itemType?.toLowerCase() === "addon"
    ) || []
  );
}, [platformItemDetails]);

  // Effects
  useEffect(() => {
    let updated = false;

    if (initialBillingPlatformId && initialBillingPlatformId !== billingPlatformId) {
      setBillingPlatformId(initialBillingPlatformId);
      updated = true;
    }

    if (initialPlanId && initialPlanId !== planId) {
      setPlanId(initialPlanId);
      updated = true;
    }

    if (initialFrequency && initialFrequency !== frequency) {
      setFrequency(initialFrequency);
      updated = true;
    }

    if (initialPrice && initialPrice !== price) {
      setPrice(initialPrice);
      updated = true;
    }

    if (initialCurrency && initialCurrency !== currency) {
      setCurrency(initialCurrency);
      updated = true;
    }

    if (options.initialAddons?.length && JSON.stringify(options.initialAddons) !== JSON.stringify(selectedAddons)) {
      setSelectedAddons(options.initialAddons);
      updated = true;
    }

    if (updated) {
      devLog('Updated hook state from initial props');
    }
  }, [
    initialBillingPlatformId,
    initialPlanId,
    initialFrequency,
    initialPrice,
    initialCurrency,
    options.initialAddons,
    billingPlatformId,
    planId,
    frequency,
    price,
    currency,
    selectedAddons
  ]);

  const shouldResolveGeoForInitialEstimate = useMemo(() => {
    const hasBillingCountry = Boolean(formData?.billingAddress?.country);
    const isBRLCurrency = itemDetails?.currencyCode === "BRL" || currency === "BRL";
    const normalizedPlatformName =
      (itemDetails?.billingPlatformName ||
        checkoutPageConfDto?.billingPlatformName ||
        platformItemDetails?.[0]?.billingPlatformName ||
        "")
        .toString()
        .trim()
        .toLowerCase();
    const providerRequiresCountry = COUNTRY_REQUIRED_ESTIMATE_PROVIDERS.has(
      normalizedPlatformName
    );
    const hasEstimatePrerequisites =
      Boolean(planId) &&
      Array.isArray(platformItemDetails) &&
      platformItemDetails.length > 0;

    return (
      providerRequiresCountry &&
      hasEstimatePrerequisites &&
      !hasBillingCountry &&
      !isBRLCurrency
    );
  }, [
    formData?.billingAddress?.country,
    itemDetails?.currencyCode,
    itemDetails?.billingPlatformName,
    checkoutPageConfDto?.billingPlatformName,
    currency,
    planId,
    platformItemDetails,
  ]);

  useEffect(() => {
    if (!shouldResolveGeoForInitialEstimate) return;
    if (geoLookupAttemptedRef.current) return;
    geoLookupAttemptedRef.current = true;

    // Fetch client IP + country via CORS-friendly geo lookups. The country is
    // used to preselect the billing country dropdown and to compute the
    // initial tax estimate before the consumer fills the billing address.
    //
    // Free geo services come and go; we try a small chain of known-good ones
    // and accept the first successful response. Each provider returns an
    // ISO-3166-1 alpha-2 country code. If every provider fails we fall back
    // to ipify for the IP alone and leave the country blank.
    const resolveGeo = async () => {
      const providers = [
        {
          url: "https://api.country.is/",
          parse: (d) => ({ ip: d?.ip, country: d?.country }),
        },
        {
          url: "https://freeipapi.com/api/json/",
          parse: (d) => ({ ip: d?.ipAddress, country: d?.countryCode }),
        },
        {
          url: "https://get.geojs.io/v1/ip/geo.json",
          parse: (d) => ({ ip: d?.ip, country: d?.country_code }),
        },
      ];

      for (const provider of providers) {
        try {
          const response = await fetch(provider.url);
          if (!response.ok) continue;
          const data = await response.json();
          const { ip, country } = provider.parse(data) || {};
          if (country) {
            return { ip: ip || "", country: country.toUpperCase() };
          }
        } catch {
          // try next provider
        }
      }
      return null;
    };

    const fetchClientGeo = async () => {
      const geo = await resolveGeo();
      if (geo) {
        setClientIp(geo.ip);
        setClientCountry(geo.country);
        return;
      }
      // Fallback: IP only (no country).
      setClientCountry("");
      try {
        const fallback = await fetch("https://api.ipify.org?format=json");
        const data = await fallback.json();
        setClientIp(data?.ip || "");
      } catch {
        setClientIp("");
      }
    };
    fetchClientGeo();
  }, [shouldResolveGeoForInitialEstimate, setClientIp, setClientCountry]);

  // Auto-select language based on currency
  useEffect(() => {
    if (currency) {
      autoSelectLanguageFromCurrency(currency);
    }
  }, [currency, autoSelectLanguageFromCurrency]);

  useEffect(() => {
    if (!platformItemDetails?.length) return;

    const billingPlatformName =
      platformItemDetails[0]?.billingPlatformName ||
      itemDetails?.billingPlatformName ||
      checkoutPageConfDto?.billingPlatformName;

    setSelectedAddons((prev) => {
      const configured = (prev?.length ? prev : options.initialAddons) || [];
      if (!configured.length) return prev;

      const next = configured.map((sel) => {
        const itemPriceId = sel.itemPriceId || sel.id;
        const catalog = platformItemDetails.find(
          (p) => p.id === itemPriceId || p.code === itemPriceId
        );
        const quantity = resolveLineItemQuantity(
          billingPlatformName,
          catalog,
          sel.quantity
        );
        if (!catalog) {
          return { ...sel, itemPriceId, quantity };
        }
        return {
          ...sel,
          itemPriceId,
          quantity,
          unitPrice: catalog.price,
          planName: catalog.planName,
          externalName: catalog.externalName,
          description: catalog.description,
          pricingModel: catalog.pricingModel,
          quantityAllowed: catalog.quantityAllowed,
          periodUnit: catalog.periodUnit,
          currencyCode: catalog.currencyCode,
          billingPlatformName: catalog.billingPlatformName,
        };
      });

      const selectionKey = (rows) =>
        JSON.stringify(rows.map((r) => ({ id: r.itemPriceId, q: r.quantity })));
      if (selectionKey(prev) === selectionKey(next)) {
        return prev;
      }
      return next;
    });
  }, [
    platformItemDetails,
    options.initialAddons,
    itemDetails?.billingPlatformName,
    checkoutPageConfDto?.billingPlatformName,
  ]);

  // BRL checkouts always bill in Brazil — set country early so estimate runs once.
  useEffect(() => {
    const isBrl = currency === "BRL" || itemDetails?.currencyCode === "BRL";
    if (!isBrl) return;
    if (formData?.billingAddress?.country === "BR") return;
    setFormData((prev) => ({
      ...prev,
      billingAddress: {
        ...prev.billingAddress,
        country: "BR",
      },
    }));
  }, [currency, itemDetails?.currencyCode, formData?.billingAddress?.country]);

  useEffect(() => {
    const fetchAll = async () => {
      if (domainStatus !== "valid") return;
      if (catalogRequiresSessionId && !sessionId) return;
      if (catalogRequiresSessionId && !resolvedBillingPlatformId) return;

      const requestId = ++catalogRequestIdRef.current;
      const isInitialCatalogLoad = !catalogLoadedOnceRef.current;

      // Only show bootstrapping / unmount skin on the first catalog load.
      // Later refetches (e.g. session restart) update in place.
      if (isInitialCatalogLoad) {
        setIsLoading(true);
        setIsRoutingLoaded(false);
      }

      if (resolvedCurrency) {
        setItemDetails((prev) => ({
          ...prev,
          name: prev?.planName || "",
          price: resolvedPrice ?? prev.price,
          period: prev.period || "",
          periodUnit: resolvedFrequency || prev.periodUnit || "",
          currencyCode: resolvedCurrency,
        }));
      }

      let routingFetched = false;
      if (resolvedBillingPlatformId) {
        const response = await fetchDetails(
          resolvedBillingPlatformId,
          resolvedPlanId,
          sessionId
        );
        routingFetched = response != null;
      } else if (!catalogRequiresSessionId) {
        await fetchDetailByTenant();
        routingFetched = true;
      }

      if (requestId !== catalogRequestIdRef.current) return;

      setIsLoading(false);
      setIsRoutingLoaded(routingFetched);
      if (routingFetched) {
        catalogLoadedOnceRef.current = true;
      }
    };

    fetchAll();
    // Currency / frequency / price only seed optimistic itemDetails; they must
    // not retrigger the catalog GET (that was a common double-fetch source).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    resolvedBillingPlatformId,
    resolvedPlanId,
    domainStatus,
    sessionId,
    catalogRequiresSessionId,
  ]);

  useEffect(() => {
    // One mount pass: return-flow UI + a single domain check (success URLs
    // previously ran validateCurrentDomain twice via two separate effects).
    const queryParams = new URLSearchParams(window.location.search);
    const status = queryParams.get("status");
    const statusLower = status?.toLowerCase();
    const orderId = queryParams.get("order_id");
    const trxId = queryParams.get("trx_id");

    if (statusLower === "processing") {
      setIsPaymentProcessingModalVisible(true);
      setPaymentProcessingHeader("Processing your payment...");
      setPaymentProcessingMessage("Please wait while we process your payment.");
      setTransactionId(orderId || trxId || "");
    } else if (statusLower === "success") {
      setTransactionId(orderId || trxId || "");
      setIsTransactionDetailsModalVisible(true);
    }

    setCurrentDomain(extractDomain());
    validateCurrentDomain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // API Calls
  const validateCurrentDomain = async () => {
    try {
      const response = await checkDomain();
      setDomainStatus(response?.status === 200 ? "valid" : "invalid");
    } catch (error) {
      toast.error("Something went wrong. Please try again later.");
      setDomainStatus("invalid");
    }
  };

  const fetchDetails = async (id, planId, catalogSessionId = sessionId) => {
    try {
      const data = await fetchCheckoutDetails(id, planId, catalogSessionId);
      if (!data) return null;

      const itemPrices = data.itemPrices || [];
      const planPrice = itemPrices.find(p => p.itemType?.toLowerCase() === "plan");
      // Do not clobber a return-flow trx/order id already taken from the URL.
      const catalogTransactionId = data?.transactionId;
      if (catalogTransactionId) {
        setTransactionId((prev) => prev || catalogTransactionId);
      }
      setCheckoutPageConfDto(data?.checkoutPageConfDto);
      setCheckoutPageFields(data?.checkoutPageFields);
      setRoutingEntries(data?.routingEntries || []);
      setPlatformItemDetails(data?.itemPrices);
      if (planPrice) {
        setItemDetails(prev => ({
          ...prev,
          billingPlatformName: data?.checkoutPageConfDto?.billingPlatformName,
          planName: planPrice.planName,
          period: planPrice.period,
          periodUnit: planPrice.periodUnit,
          currencyCode:
            planPrice.currencyCode ||
            resolvedCurrency ||
            prev.currencyCode ||
            "",
        }));
        if (planPrice.status !== "active") {
          setErrorModalVisible(true);
        }
      }
      updateMetadata(data?.checkoutPageConfDto);
      return data;
    } catch (error) {
      toast.error("Something went wrong. Please try again later.");
      return null;
    }
  };

  const fetchDetailByTenant = async ({ seedOnly = false } = {}) => {
    try {
      const data = await fetchCheckoutConf();
      if (!data) return;
      // seedOnly: fill checkoutPageConfDto from /conf before catalog exists so
      // error screens can read checkoutRedirectUrl from the same DTO. Do not
      // clobber a richer catalog DTO that already arrived.
      setCheckoutPageConfDto((prev) => (seedOnly && prev ? prev : data));
      setLogoUrl(data?.logoUrl);
      setBrandColor(data?.colour || "");
      setMerchantName(data?.companyName || data?.brand || "");
      updateMetadata(data);
    } catch (error) {
      if (!seedOnly) {
        toast.error("Something went wrong. Please try again later.");
      }
    }
  };

  // Hosted checkout waits on session before catalog, but /conf is available
  // immediately — seed checkoutPageConfDto so Return CTAs work on early errors.
  useEffect(() => {
    if (domainStatus !== "valid") return;
    fetchDetailByTenant({ seedOnly: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainStatus]);

  // Form Handlers
  const handleFormChange = (newData) => {
    const updatedData = { ...formData, ...newData };
    setFormData(updatedData);
  };

  const handleValidationChange = (validations) => {
    setCustomerFormValid(validations?.isFormValid || false);
  };

  // Single source of truth for the CTA: the customer form must be valid
  // (CustomerInfoForm already respects required/optional fields) and a
  // payment method must be selected.
  useEffect(() => {
    setIsFormValid(Boolean(customerFormValid && formData?.paymentMethod));
  }, [customerFormValid, formData?.paymentMethod]);

  const validateForm = (data) => {
    const requiredFields = checkoutPageFields || [];

    const fieldMapping = {
      FIRST_NAME: data?.firstName,
      LAST_NAME: data?.lastName,
      EMAIL: data?.email,
      BILLING_FIRST_NAME: data?.billingAddress?.firstName,
      BILLING_LAST_NAME: data?.billingAddress?.lastName,
      BILLING_ADDRESS_LINE_1: data?.billingAddress?.street,
      BILLING_ADDRESS_LINE_2: data?.billingAddress?.addressLine2,
      BILLING_CITY: data?.billingAddress?.city,
      BILLING_ZIPCODE: data?.billingAddress?.zipCode,
      BILLING_COUNTRY: data?.billingAddress?.country,
      BILLING_STATE: data?.billingAddress?.state,
      SHIPPING_FIRST_NAME: data?.shippingAddress?.firstName,
      SHIPPING_LAST_NAME: data?.shippingAddress?.lastName,
      SHIPPING_ADDRESS_LINE_1: data?.shippingAddress?.street,
      SHIPPING_ADDRESS_LINE_2: data?.shippingAddress?.addressLine2,
      SHIPPING_CITY: data?.shippingAddress?.city,
      SHIPPING_ZIPCODE: data?.shippingAddress?.zipCode,
      SHIPPING_COUNTRY: data?.shippingAddress?.country,
      SHIPPING_STATE: data?.shippingAddress?.state,
    };

    // checkoutPageFields may contain UI-only toggles (e.g. COUPON_CODE)
    // that are not part of customer form data. Ignore those here.
    const customerFields = requiredFields.filter((field) =>
      Object.prototype.hasOwnProperty.call(fieldMapping, field)
    );

    const isValid =
      data?.firstName &&
      data?.lastName &&
      data?.email &&
      customerFields.every((field) => {
        const value = fieldMapping[field];
        return value != null && String(value).trim().length > 0;
      }) &&
      data?.paymentMethod;
    return Boolean(isValid);
  };

  const handleSubmit = () => {
    if (isFormValid) {
      // Submit logic here
      // alert("You will be redirected to a secure payment page");
    }
  };

  // Expose setters for external use (e.g., AltruonCheckoutPage session fetching)
  const updateBillingData = (billingPlatformId, planId, frequency, price, currency) => {
    setBillingPlatformId(billingPlatformId);
    setPlanId(planId);
    setFrequency(frequency);
    setPrice(price);
    setCurrency(currency);
  };

  return {
    // State
    checkoutPageConfDto,
    checkoutPageFields,
    routingEntries,
    platformItemDetails,
    isErrorModalVisible,
    itemDetails,
    showModal,
    transactionStatus,
    domainStatus,
    currentDomain,
    isLoading,
    formData,
    isFormValid,
    isRoutingLoaded,
    transactionId,
    isPaymentProcessingModalVisible,
    isTransactionDetailsModalVisible,
    paymentProcessingHeader,
    paymentProcessingMessage,
    billingPlatformId,
    planId,
    frequency,
    price,
    currency,
    addons,
    selectedAddons,

    // Setters
    setErrorModalVisible,
    setSelectedAddons,
    setShowModal,
    setTransactionStatus,
    setIsPaymentProcessingModalVisible,
    setIsTransactionDetailsModalVisible,
    setPaymentProcessingHeader,
    setPaymentProcessingMessage,
    setTransactionId,
    updateBillingData,
    setCheckoutPageConfDto,

    // Handlers
    handleFormChange,
    handleValidationChange,
    validateForm,
    handleSubmit,

    // API methods (exposed for custom use cases)
    fetchDetails,
    fetchDetailByTenant,
    validateCurrentDomain,
  };
};

